import {
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { Stack, useTheme } from "@mui/system";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { FilterGroup } from "../../../components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "../../../components/OmnisearchDataGrid";
import { WASTE_SUBMISSIONS_FOR_STAFF_QUERY } from "./api";
import { BulkActionsMenu } from "./BulkActionsMenu";
import { getMaterializationType } from "./utils";
import { prettyPrintDateMed, prettyPrintDateTimeMed } from "util/dates";
import {
  WasteSubmissionsForStaffQuery,
  WasteSubmissionsForStaffQueryVariables,
  SubmissionStatus,
  SubmissionType,
  VirtualEventStatus,
  Frequency,
  SubmissionMaterializationType,
} from "generated-graphql/graphql";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PostAddIcon from "@mui/icons-material/PostAdd";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { indigo, grey, amber } from "@mui/material/colors";
import { Link, useNavigate } from "react-router-dom";

// Generator status display configuration
const GENERATOR_STATUS_CONFIG: Record<
  string,
  { label: string; color: string; textColor: string }
> = {
  LARGE_QUANTITY_GENERATOR: {
    label: "LQG",
    color: indigo[600],
    textColor: "#fff",
  },
  SMALL_QUANTITY_GENERATOR: {
    label: "SQG",
    color: indigo[300],
    textColor: "#000",
  },
  VERY_SMALL_QUANTITY_GENERATOR: {
    label: "VSQG",
    color: indigo[200],
    textColor: "#000",
  },
  NONE: {
    label: "None",
    color: grey[300],
    textColor: "#000",
  },
};

export type Row =
  WasteSubmissionsForStaffQuery["wasteSubmissionsForStaff"]["items"][number];

function getRowHref(row: Row): string | null {
  if (!row.taskId || !row.deadline) return null;

  const isMaterializedInstance = row.taskFrequency === Frequency.Once;

  if (isMaterializedInstance && row.submissionId) {
    return `/staff/fulfillment/waste/submissions/${row.submissionId}`;
  }

  if (!row.tenantId) return null;
  const dateOnly = row.deadline.split("T")[0];
  return `/o/${row.tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`;
}

function getFulfillmentDetailHref(row: Row): string | null {
  if (row.taskFrequency !== Frequency.Once || !row.submissionId) return null;
  return `/staff/fulfillment/waste/submissions/${row.submissionId}`;
}

function getCustomerSubmissionHref(row: Row): string | null {
  if (
    row.taskFrequency !== Frequency.Once ||
    !row.submissionId ||
    !row.tenantId
  ) {
    return null;
  }
  return `/o/${row.tenantId}/waste/submissions/${row.submissionId}/edit`;
}

function getStatusDisplay(status: string): string {
  switch (status) {
    case "NOT_STARTED":
      return "Not Started";
    case "IN_PROGRESS":
      return "In Progress";
    case "ENCAMP_INITIAL_REVIEW":
      return "Initial Review";
    case "IN_REVIEW":
      return "In Review";
    case "PROCESSING_SUBMISSION":
      return "Processing";
    case "FILING_COMPLETE":
      return "Complete";
    case "NOT_REPORTING":
      return "Not Reporting";
    default:
      return status;
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case "NOT_STARTED":
      return "#f57c00"; // orange
    case "IN_PROGRESS":
      return "#1976d2"; // blue
    case "ENCAMP_INITIAL_REVIEW":
      return "#9c27b0"; // purple
    case "IN_REVIEW":
      return "#0288d1"; // light blue
    case "PROCESSING_SUBMISSION":
      return "#7b1fa2"; // deep purple
    case "FILING_COMPLETE":
      return "#388e3c"; // green
    case "NOT_REPORTING":
      return "#757575"; // grey
    default:
      return "#757575";
  }
}

// Check if the virtual event is overdue
function isOverdue(row: Row): boolean {
  return row.virtualEventStatus === VirtualEventStatus.Overdue;
}

interface WasteSubmissionsDataGridProps {
  includeOverdue: boolean;
  onlyOverdue: boolean;
  startDate: string;
  endDate: string;
  /**
   * Filter by materialization type(s).
   * Allows filtering to VIRTUAL_EVENT, MATERIALIZED, ONE_OFF or any combination.
   */
  materializationTypes?: SubmissionMaterializationType[];
  /**
   * Default search string to apply when omnisearch is empty.
   * This allows default filters (status, date range, etc.) to be applied
   * consistently with the filter bar UI.
   */
  defaultSearch?: string;
}

export function WasteSubmissionsDataGrid({
  includeOverdue,
  onlyOverdue,
  startDate,
  endDate,
  materializationTypes,
  defaultSearch,
}: WasteSubmissionsDataGridProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const omnisearchInputRef = useRef<HTMLInputElement>(null);
  const refetchRef = useRef<(() => void) | null>(null);
  useOmnisearchDatagrid({
    persistenceKey: "waste-submissions-staff-v2",
    isURLDriven: true,
    initialSortModel: [{ field: "deadline", sort: "asc" }],
  });

  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  const handleSelectedRowsChanged = useCallback((rows: Row[]) => {
    setSelectedRows(rows);
  }, []);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "tenantName",
        headerName: "Organization",
        width: 180,
        filterKey: "tenant",
        renderCell: ({ row }) => {
          const href = getRowHref(row);
          if (!href) {
            return (
              <Tooltip title={row.tenantName}>
                <Typography noWrap variant="body2">
                  {row.tenantName}
                </Typography>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={row.tenantName}>
              <Typography
                noWrap
                variant="body2"
                component={Link}
                to={href}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {row.tenantName}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "facilityName",
        headerName: "Facility",
        width: 180,
        filterKey: "facility",
        renderCell: ({ row }) => {
          const primaryFacility = row.facilities?.[0];
          const href = getRowHref(row);
          if (!href) {
            return (
              <Tooltip
                title={`${row.facilityName ?? ""}\n${primaryFacility?.state ?? ""}`}
              >
                <Typography noWrap variant="body2">
                  {row.facilityName ?? "—"}
                </Typography>
              </Tooltip>
            );
          }

          return (
            <Tooltip
              title={`${row.facilityName ?? ""}\n${primaryFacility?.state ?? ""}`}
            >
              <Typography
                noWrap
                variant="body2"
                component={Link}
                to={href}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {row.facilityName ?? "—"}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "epaIds",
        headerName: "EPA ID",
        width: 130,
        filterKey: "epaId",
        sortable: false,
        renderCell: ({ row }) => {
          const epaIds = row.epaIds ?? [];
          if (epaIds.length === 0) return "—";
          if (epaIds.length === 1) return epaIds[0];
          return (
            <Tooltip title={epaIds.join(", ")}>
              <span>{epaIds[0]}...</span>
            </Tooltip>
          );
        },
      },
      {
        field: "state",
        headerName: "State",
        width: 70,
        // filterKey defined in additionalFilterColumns
      },
      {
        field: "submissionType",
        headerName: "Type",
        width: 150,
        // filterKey defined in additionalFilterColumns with enum type
        renderCell: ({ row }) => (
          <Tooltip title={row.submissionType}>
            <Typography noWrap variant="body2">
              {prettyPrintEnumValue(row.submissionType)}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "submissionStatus",
        headerName: "Status",
        width: 130,
        // filterKey defined in additionalFilterColumns with enum type
        renderCell: ({ row }) => (
          <Chip
            label={getStatusDisplay(row.submissionStatus)}
            size="small"
            sx={{
              backgroundColor: alpha(getStatusColor(row.submissionStatus), 0.15),
              color: getStatusColor(row.submissionStatus),
              fontWeight: 500,
            }}
          />
        ),
      },
      {
        field: "materializationType",
        headerName: "",
        width: 40,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const materializationType = getMaterializationType(row);

          if (materializationType === SubmissionMaterializationType.VirtualEvent) {
            const requirementsLink = `/staff/requirements?omnisearch=taskId:${row.taskId}`;
            return (
              <Tooltip title="Virtual event - click to view requirement">
                <IconButton
                  component={Link}
                  to={requirementsLink}
                  target="_blank"
                  size="small"
                  sx={{ p: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CloudQueueIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </IconButton>
              </Tooltip>
            );
          }
          
          if (materializationType === SubmissionMaterializationType.OneOff) {
            return (
              <Tooltip title="One-off submission - no linked requirement">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                  }}
                >
                  <PostAddIcon
                    fontSize="small"
                    sx={{ color: amber[700], opacity: 0.8 }}
                  />
                </Box>
              </Tooltip>
            );
          }
          
          // MATERIALIZED
          const requirementsLink = `/staff/requirements?omnisearch=seriesId:${row.seriesId}`;
          return (
            <Tooltip title="Materialized instance - click to view requirement">
              <IconButton
                component={Link}
                to={requirementsLink}
                target="_blank"
                size="small"
                sx={{ p: 0.5 }}
                onClick={(e) => e.stopPropagation()}
              >
                <CheckCircleOutlineIcon
                  fontSize="small"
                  sx={{ color: "success.main", opacity: 0.7 }}
                />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 96,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const customerHref = getCustomerSubmissionHref(row);
          const fulfillmentDetailHref = getFulfillmentDetailHref(row);

          return (
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Tooltip
                title={
                  customerHref
                    ? "Open customer submission"
                    : "Customer submission unavailable"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    component={customerHref ? Link : "button"}
                    to={customerHref ?? undefined}
                    target={customerHref ? "_blank" : undefined}
                    rel={customerHref ? "noopener noreferrer" : undefined}
                    disabled={!customerHref}
                    onClick={(e: MouseEvent<HTMLElement>) =>
                      e.stopPropagation()
                    }
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  fulfillmentDetailHref
                    ? "Open fulfillment detail"
                    : "Fulfillment detail unavailable"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    component={fulfillmentDetailHref ? Link : "button"}
                    to={fulfillmentDetailHref ?? undefined}
                    disabled={!fulfillmentDetailHref}
                    onClick={(e: MouseEvent<HTMLElement>) =>
                      e.stopPropagation()
                    }
                  >
                    <DescriptionOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        field: "waitingOn",
        headerName: "Customer Next Action",
        width: 160,
        sortable: false,
        renderCell: ({ row }) => {
          const waitingOn = row.waitingOn ?? [];
          if (waitingOn.length === 0) {
            // Show contextual message based on status
            const status = row.submissionStatus;
            if (status === "NOT_STARTED" || status === "IN_PROGRESS") {
              return (
                <Typography noWrap variant="body2" color="text.secondary">
                  Encamp
                </Typography>
              );
            }
            if (status === "PROCESSING_SUBMISSION") {
              return (
                <Typography noWrap variant="body2" color="text.secondary">
                  Ready to file
                </Typography>
              );
            }
            if (status === "FILING_COMPLETE" || status === "NOT_REPORTING") {
              return "—";
            }
            return "—";
          }
          if (waitingOn.length === 1) {
            const user = waitingOn[0];
            const name = user.firstName
              ? `${user.firstName} ${user.lastName ?? ""}`.trim()
              : user.email;
            return (
              <Tooltip title={user.isSigner ? "Signer" : "Reviewer"}>
                <Typography noWrap variant="body2">
                  {name}
                  {user.isSigner && " (Sign)"}
                </Typography>
              </Tooltip>
            );
          }
          return (
            <Tooltip
              title={waitingOn
                .map((u) =>
                  u.firstName ? `${u.firstName} ${u.lastName}` : u.email
                )
                .join(", ")}
            >
              <Typography noWrap variant="body2">
                {waitingOn.length} reviewers
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "deadline",
        headerName: "Due Date",
        width: 110,
        filterKey: "dueDate",
        renderCell: ({ row }) => (
          <Typography
            noWrap
            variant="body2"
            color={isOverdue(row) ? "error" : "text.primary"}
            fontWeight={isOverdue(row) ? 600 : 400}
          >
            {row.deadline ? prettyPrintDateMed(row.deadline) : "—"}
          </Typography>
        ),
      },
      {
        field: "ownerName",
        headerName: "Encamp Assignee",
        width: 140,
        filterKey: "staffAssignee",
        renderCell: ({ row }) => (
          <Tooltip title={row.ownerEmail ?? ""}>
            <Typography noWrap variant="body2">
              {row.ownerName ?? "—"}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "peerReviewerName",
        headerName: "Peer Reviewer",
        width: 140,
        filterKey: "peerReviewer",
        renderCell: ({ row }) => (
          <Tooltip title={row.peerReviewerEmail ?? ""}>
            <Typography noWrap variant="body2">
              {row.peerReviewerName ?? "—"}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "generatorStatus",
        headerName: "Generator",
        width: 90,
        filterKey: "generatorStatus",
        renderCell: ({ row }) => {
          if (!row.generatorStatus) return "—";
          const config = GENERATOR_STATUS_CONFIG[row.generatorStatus];
          if (!config) {
            return (
              <Typography noWrap variant="body2">
                {row.generatorStatus}
              </Typography>
            );
          }
          return (
            <Chip
              label={config.label}
              size="small"
              sx={{
                backgroundColor: config.color,
                color: config.textColor,
                fontWeight: 500,
                fontSize: "0.75rem",
                height: 24,
              }}
            />
          );
        },
      },
      {
        field: "lastInternalComment",
        headerName: "Internal Comment",
        width: 250,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.lastInternalComment) return "—";
          return (
            <Tooltip
              title={
                <Stack spacing={0.5}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {row.lastInternalCommentByName ?? "Unknown"} • {row.lastInternalCommentAt ? prettyPrintDateTimeMed(row.lastInternalCommentAt) : ""}
                  </Typography>
                  <Typography variant="body2">{row.lastInternalComment}</Typography>
                </Stack>
              }
            >
              <Typography noWrap variant="body2">
                <Typography component="span" variant="body2" color="text.secondary">
                  {row.lastInternalCommentByName ?? "Unknown"}:
                </Typography>{" "}
                {row.lastInternalComment}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "lastComment",
        headerName: "Customer Comment",
        width: 250,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.lastComment) return "—";
          return (
            <Tooltip
              title={
                <Stack spacing={0.5}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {row.lastCommentByName ?? "Unknown"} • {row.lastCommentAt ? prettyPrintDateTimeMed(row.lastCommentAt) : ""}
                  </Typography>
                  <Typography variant="body2">{row.lastComment}</Typography>
                </Stack>
              }
            >
              <Typography noWrap variant="body2">
                <Typography component="span" variant="body2" color="text.secondary">
                  {row.lastCommentByName ?? "Unknown"}:
                </Typography>{" "}
                {row.lastComment}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "lastActivity",
        headerName: "Last Activity",
        width: 140,
        renderCell: ({ row }) => (
          <Typography noWrap variant="body2">
            {row.lastActivity ? prettyPrintDateTimeMed(row.lastActivity) : "—"}
          </Typography>
        ),
      },
    ],
    []
  );

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      // Column 1: Organization & Facility
      {
        name: "Organization & Facility",
        fields: ["tenant", "facility", "epaId"],
      },
      {
        name: "Location",
        fields: ["state"],
      },
      // Column 2: Submission Details
      {
        name: "Submission",
        fields: ["submissionType", "status", "waitingOn", "dueDate"],
      },
      {
        name: "Generator Status",
        fields: ["generatorStatus"],
      },
      // Column 3: Staff Assignment
      {
        name: "Staff Assignment",
        fields: ["staffAssignee", "peerReviewer"],
      },
      {
        name: "Workflow",
        fields: ["assignedToMe"],
      },
    ],
    []
  );

  const handleRefetch = useCallback(() => {
    refetchRef.current?.();
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  // Separate materialized submission IDs from virtual event IDs for bulk actions
  // - materializedSubmissionIds: actual submission IDs for rows that have been truly started
  // - selectedVirtualEventIds: virtual event IDs (taskId_deadline format) for rows NOT yet started
  //
  // CRITICAL: We check submissionStatus, NOT just submissionId presence!
  // Virtual events from recurring tasks share a "template" submission, so they have a submissionId
  // but that ID points to the template, not their own submission. If we updated the template,
  // ALL virtual events in the series would be affected.
  //
  // Only rows with status OTHER than NOT_STARTED have been truly materialized with their own submission.
  const { materializedSubmissionIds, selectedVirtualEventIds } = useMemo(() => {
    const materialized: string[] = [];
    const virtual: string[] = [];

    for (const row of selectedRows) {
      // A row is truly materialized only if:
      // 1. It has a submissionId AND
      // 2. Its status is NOT NOT_STARTED (meaning work has actually started on it)
      const isActuallyMaterialized =
        row.submissionId != null &&
        row.submissionStatus !== SubmissionStatus.NotStarted;

      if (isActuallyMaterialized && row.submissionId) {
        materialized.push(row.submissionId);
      } else {
        // Either no submission or NOT_STARTED status means this is a virtual event
        // that needs to be materialized when performing actions
        virtual.push(row.id);
      }
    }

    return {
      materializedSubmissionIds: materialized,
      selectedVirtualEventIds: virtual,
    };
  }, [selectedRows]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Stack
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
        spacing={2}
        px={3}
        paddingTop={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body1" color={theme.palette.text.secondary}>
          Waste Submissions
        </Typography>
      </Stack>
      <Box
        sx={{
          height: "100vh",
          pt: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <OmnisearchDataGrid<
          WasteSubmissionsForStaffQuery,
          WasteSubmissionsForStaffQueryVariables,
          Row
        >
          omnisearchInputRef={omnisearchInputRef}
          autoFocus={false}
          isURLDriven
          persistenceKey="waste-submissions-staff-v2"
          columns={columns}
          columnVisibilityModel={{}}
          commandButtons={[
            <Stack
              key="command-buttons"
              id="command-buttons"
              sx={{
                marginBottom: "1rem",
                marginTop: "1rem",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
              }}
              spacing={2}
              alignItems="center"
            >
              <Stack
                direction={"row"}
                justifyContent={"flex-end"}
                marginTop={0}
                sx={{ flex: { sm: 1, lg: 2 }, alignItems: "center" }}
              >
                <BulkActionsMenu
                  selectedRows={selectedRows}
                  selectedIds={selectedRows.map((r) => r.id)}
                  selectedSubmissionIds={materializedSubmissionIds}
                  selectedVirtualEventIds={selectedVirtualEventIds}
                  onSuccess={handleRefetch}
                  onClearSelection={handleClearSelection}
                />
              </Stack>
            </Stack>,
          ]}
          dataQuery={WASTE_SUBMISSIONS_FOR_STAFF_QUERY}
          defaultSearch={defaultSearch}
          dataQueryVariables={{ includeOverdue, onlyOverdue, startDate, endDate, materializationTypes }}
          checkboxSelection
          disableRowSelectionOnClick
          onRowClick={useCallback(
            ({ row }: { row: Row }) => {
              const href = getRowHref(row);
              if (!href) return;
              navigate(href);
            },
            [navigate]
          )}
          initialSortModel={[{ field: "deadline", sort: "asc" }]}
          getItems={(data) => data.wasteSubmissionsForStaff.items}
          getCount={(data) => data.wasteSubmissionsForStaff.count}
          noDataMessage="No waste submissions found for the current search."
          initialPageSize={50}
          filterGroups={filterGroups}
          wideLayout
          selectedIds={selectedRows.map((r) => r.id)}
          onSelectedRowsChanged={handleSelectedRowsChanged}
          additionalFilterColumns={[
            {
              header: "Organization",
              key: "tenant",
              filterKeyType: "multipleTenant",
            },
            {
              header: "Facility",
              key: "facility",
              filterKeyType: "facility",
            },
            { header: "State", key: "state" },
            {
              header: "Type",
              key: "submissionType",
              filterKeyType: "enum",
              enumValues: Object.values(SubmissionType),
            },
            {
              header: "Status",
              key: "status",
              filterKeyType: "enum",
              enumValues: Object.values(SubmissionStatus),
            },
            { header: "EPA ID", key: "epaId" },
            { header: "Encamp Assignee", key: "staffAssignee" },
            { header: "Peer Reviewer", key: "peerReviewer" },
            { header: "Assigned to Me", key: "assignedToMe", filterKeyType: "boolean" },
            {
              header: "Type",
              key: "materializationType",
              filterKeyType: "enum",
              enumValues: Object.values(SubmissionMaterializationType),
            },
          ]}
          fetchPolicy="no-cache"
          onRefetchReady={(refetch) => {
            refetchRef.current = refetch;
          }}
          excludeFilterColumns={["tenantName", "facilityName"]}
          gridSx={{
            height: "100%",
            minHeight: "400px",
            maxHeight: "calc(100vh - 280px)",
          }}
        />
      </Box>
    </Paper>
  );
}
